import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '../../types'

const DEFAULT_API_URL = '/api'

const AUTH_STORAGE_KEY = 'auth-storage'

// La session est en sessionStorage (pas localStorage) : pas de reconnexion
// automatique après fermeture du navigateur
function getStoredToken(): string | null {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { token?: string } }
    return parsed?.state?.token ?? null
  } catch { return null }
}

function getStoredRefreshToken(): string | null {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { refreshToken?: string } }
    return parsed?.state?.refreshToken ?? null
  } catch { return null }
}

const isErrorPayload = (payload: unknown): payload is { message?: unknown } =>
  typeof payload === 'object' && payload !== null

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? DEFAULT_API_URL,
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
})

// Attach token on every request
api.interceptors.request.use(config => {
  const token = getStoredToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Queue pour éviter plusieurs refresh simultanés
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(p => {
    if (error) p.reject(error)
    else p.resolve(token!)
  })
  failedQueue = []
}

function forceLogout() {
  sessionStorage.removeItem(AUTH_STORAGE_KEY)
  localStorage.removeItem('test-user-id')
  // Importer le store ici causerait une dépendance circulaire — on dispatch un event custom
  window.dispatchEvent(new Event('auth:logout'))
  window.location.replace('/login')
}

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    const requestUrl = originalRequest?.url ?? ''
    const isCredentialAuth =
      /\/auth\/(login|register)(\?|$)/.test(requestUrl)

    // Un 401 login/register est un identifiant faux, pas une session expirée
    if (error.response?.status === 401 && !originalRequest._retry && !isCredentialAuth) {
      const refreshToken = getStoredRefreshToken()

      if (!refreshToken) {
        forceLogout()
        return Promise.reject(buildApiError(error))
      }

      if (isRefreshing) {
        // Mettre en file d'attente
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL ?? DEFAULT_API_URL}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        )

        const data = response.data as { data?: { accessToken?: string; refreshToken?: string } }
        const newAccessToken = data?.data?.accessToken
        const newRefreshToken = data?.data?.refreshToken

        if (!newAccessToken) throw new Error('No token in refresh response')

        // Mettre à jour les tokens dans auth-storage (même structure que Zustand persist)
        try {
          const raw = sessionStorage.getItem(AUTH_STORAGE_KEY)
          const parsed = raw ? (JSON.parse(raw) as { state?: Record<string, unknown> }) : { state: {} }
          if (!parsed.state) parsed.state = {}
          parsed.state.token = newAccessToken
          if (newRefreshToken) parsed.state.refreshToken = newRefreshToken
          parsed.state.isAuthenticated = true
          sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed))
        } catch { /* ignore */ }

        // Mettre à jour le store sans import circulaire
        window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', {
          detail: { accessToken: newAccessToken, refreshToken: newRefreshToken ?? refreshToken }
        }))

        processQueue(null, newAccessToken)
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        forceLogout()
        return Promise.reject(buildApiError(error))
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(buildApiError(error))
  }
)

function buildApiError(error: AxiosError): ApiError {
  const payload = error.response?.data
  let message = 'Une erreur est survenue'

  if (isErrorPayload(payload) && typeof payload.message === 'string') {
    message = payload.message
  } else if (error.response?.status === 403) {
    message = 'Accès refusé'
  } else if (error.response?.status === 404) {
    message = 'Ressource introuvable'
  } else if (error.response?.status === 503) {
    message = 'Service temporairement indisponible'
  } else if (!error.response) {
    message = 'Impossible de se connecter au serveur'
  }

  return {
    message,
    code: error.response?.status?.toString() ?? 'UNKNOWN',
    details: payload,
  }
}

export async function apiCall<T>(request: Promise<AxiosResponse<T>>): Promise<T> {
  const response = await request
  return response.data
}
